import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  makeStyles,
  Button,
  Card,
  CardContent,
  IconButton,
  Grid,
  CardHeader,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import 'translations/translations';
import { useMap } from 'react-leaflet';
import { StackStateEnumDto, StackMapInfoResponse } from '.generated/api';
import { Cancel } from '@material-ui/icons';
import { stackActions } from 'actions/stackActions';
import { ApplicationState } from 'store';
import OverlaySpinner from 'components/spinners/OverlaySpinner';

const useStyles = makeStyles((theme) => ({
  fixCardPadding: {
    paddingTop: '0rem',
    paddingBottom: '0rem',
    marginTop: '-1.875rem',
    marginBotton: '-1.875rem',
  },
  paddingBottom: {
    paddingBottom: theme.spacing(2),
  },
  cardContent: {
    paddingTop: 0,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  buttonMargin: {
    margin: 0,
  },
}));

function lastChanged(stack: StackMapInfoResponse) {
  const lastModifiedDateTime = new Date(stack.lastModified);
  if (lastModifiedDateTime) {
    return lastModifiedDateTime.toLocaleDateString(navigator.language);
  }
  return '';
}

interface ParamTypes {
  orgId: string;
}

interface CardItemProps {
  label: string;
  value: string;
}
const CardItem: React.FC<CardItemProps> = ({ label, value }) => {
  return (
    <Grid item style={{ minWidth: '50%' }}>
      <Grid container direction="column">
        <Grid item>
          <b>{label}</b>
        </Grid>
        <Grid item>{value}</Grid>
      </Grid>
    </Grid>
  );
};

interface StacksMapInfoCardProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  closeSelectedStackInfo: Function;
  disableStackDetailLink?: boolean;
}

const StacksMapInfoCard: React.FC<StacksMapInfoCardProps> = ({
  closeSelectedStackInfo,
  disableStackDetailLink,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation(['translations']);
  const map = useMap();
  const dispatch = useDispatch();

  const { orgId } = useParams<ParamTypes>();

  const mapInfo = useSelector(
    (state: ApplicationState) => state.stacks.mapInfo,
  );

  const closePopup = () => {
    map.closePopup();
    closeSelectedStackInfo();
  };

  const openStackDetails = () => {
    if (mapInfo) {
      // TODO: need departmentId in mapInfo
      history.push(
        `/organization/${orgId}/department/${mapInfo.departmentId}/stack/${mapInfo.stackId}`,
      );
    }
  };

  const handleDownloadReport = () => {
    if (mapInfo) {
      dispatch(stackActions.downloadStackArchive([mapInfo.stackId]));
    }
  };

  return (
    <OverlaySpinner isLoading={!mapInfo}>
      <Card className={classes.fixCardPadding}>
        <CardHeader
          action={
            <Grid
              container
              spacing={2}
              justify="flex-end"
              alignItems="flex-start"
            >
              <Grid item>
                <IconButton onClick={() => closePopup()} color="secondary">
                  <Cancel />
                </IconButton>
              </Grid>
            </Grid>
          }
        ></CardHeader>
        <CardContent className={classes.cardContent}>
          <Grid
            container
            item
            alignItems="flex-start"
            className={classes.paddingBottom}
            spacing={2}
          >
            <CardItem
              label={t('stacks_page.table.stack_no')}
              value={mapInfo?.stackNumber?.toString() ?? ''}
            />
            <CardItem
              label={t('stacks_page.table.registrator')}
              value={mapInfo?.registrator ?? ''}
            />
            <CardItem
              label={t('stacks_page.table.surveyor')}
              value={mapInfo?.surveyor ?? ''}
            />
          </Grid>

          <Grid
            container
            justify="space-between"
            className={classes.paddingBottom}
          >
            {!disableStackDetailLink && (
              <Grid item>
                <Button
                  className={classes.buttonMargin}
                  variant="outlined"
                  color="primary"
                  onClick={() => openStackDetails()}
                >
                  {t('stacks_page.map_info_card.see_details')}
                </Button>
              </Grid>
            )}
            <Grid item>
              <Button
                className={classes.buttonMargin}
                variant="outlined"
                color="secondary"
                onClick={handleDownloadReport}
                disabled={mapInfo?.state === StackStateEnumDto.PRE_REGISTRATION}
              >
                {t('stacks_page.map_info_card.get_report')}
              </Button>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item>
              <b>{t('stacks_page.table.last_changed')}: </b>
              {mapInfo && lastChanged(mapInfo)}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </OverlaySpinner>
  );
};

export { StacksMapInfoCard };
